import { getGraphemeRender } from "@-/dev.improve.haniverse.assets.graphemes.fonts";
import runes from "@-/com.npmjs.runes2";
import { toneFromMarks } from "@-/com.npmjs.kensio__pinyinjs";
import { getPrimaryPinyinForGrapheme } from "#grapheme-pinyin-map";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export type GraphemeTone = 0 | 1 | 2 | 3 | 4;

export type GetGraphemeGlyphOptions = {
  color?: string | null;
  /** Pre-resolved display grapheme. When omitted, `display` is `original`. */
  display?: string;
};

function resolveDisplayGrapheme(original: string, display?: string): string {
  return display ?? original;
}

/** Extract tone (0-4) from either tone-number form (`ma3`) or diacritic form (`mǎ`); neutral → 0. */
export function toneForPinyin(pinyin: string): GraphemeTone | null {
  const trimmed = pinyin.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const trailing = trimmed.codePointAt(trimmed.length - 1);
  // Accept a trailing tone digit `0`–`5`; both `0` and `5` denote the neutral tone (→ 0).
  if (trailing !== undefined && trailing >= 0x30 && trailing <= 0x35) {
    const digit = trailing - 0x30;
    switch (digit) {
      case 1:
      case 2:
      case 3:
      case 4: {
        return digit;
      }
      case 0:
      case 5: {
        return 0;
      }
      default: {
        break;
      }
    }
  }
  const tone = toneFromMarks(trimmed);
  return tone === undefined || tone === 5 ? 0 : tone;
}

export function pinyinForGrapheme(grapheme: string): string | null {
  if (!hasLength(runes(grapheme), 1)) {
    return null;
  }
  return getPrimaryPinyinForGrapheme(grapheme);
}

export function getGraphemeGlyph(
  original: string,
  { color = null, display: displayOverride }: GetGraphemeGlyphOptions = {},
): GraphemeGlyph {
  const display = resolveDisplayGrapheme(original, displayOverride);
  const render = getGraphemeRender(display);
  return {
    ...(render === undefined ? {} : { render }),
    color,
    display,
    fontWeight: "400",
    original,
  };
}
